import { useState } from "react";
import * as Yup from "yup";

import * as api from "../../../../../api";
import useAsyncMemo from "../../../../../hooks/useAsyncMemo";
import useDebounce from "../../../../../hooks/useDebounce";
import useErrorAlert from "../../../../../hooks/useErrorAlert";
import getGenericErrorInfo from "../../../../../utils/getGenericErrorInfo";

const checkEmailExists = async (email: string) => {
  if (!email) {
    return undefined;
  }
  try {
    return await api.checkEmailExists(email);
  } catch (error) {
    return undefined;
  }
};

const emailSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Required"),
});

export type EmailData = Yup.Asserts<typeof emailSchema>;

export default function useEmailScreen({
  onNext,
}: {
  onNext: ({
    email,
    emailExists,
  }: {
    email: string;
    emailExists: boolean;
  }) => void;
}) {
  const [email, setEmail] = useState("");
  const emailDebounced = useDebounce(email, 500);
  const [emailExists, isCheckingEmailExists] = useAsyncMemo(
    () => checkEmailExists(emailDebounced),
    [emailDebounced]
  );

  const { setError } = useErrorAlert();

  const onSubmit = async (values: { email: string }) => {
    try {
      const emailExists_ = await api.checkEmailExists(values.email);
      onNext({ email, emailExists: emailExists_ });
    } catch (error) {
      setError(getGenericErrorInfo(error));
    }
  };

  return {
    isCheckingEmailExists:
      email && (email !== emailDebounced || isCheckingEmailExists),
    initialValues: { email: "" },
    onSubmit,
    schema: emailSchema,
    setEmail,
    emailExists: email ? emailExists : undefined,
  };
}
